// src/App.jsx

// React
import { lazy, Suspense, useEffect, useState } from "react";

// Router
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  useLocation,
  Navigate,
} from "react-router-dom";

// Components
import Navbar from "./components/Navbar";
import BottomNav from "./components/BottomNav";
import ShopFooter from "./components/ShopFooter";
import ErrorBoundary from "./components/ErrorBoundary";
import RouteScrollManager from "./components/RouteScrollManager";
import RouteTitleManager from "./components/RouteTitleManager";
import { FormSkeleton } from "./components/skeletons/ShopperSkeletons";
import ResubmitApplication from "./pages/ResubmitApplication";
import { getShopperLayoutConfig } from "./utils/shopLayout";

import { useAuth } from "./context/AuthContext";
import { useMessaging } from "./context/MessagingContext";


// Route Guards
import ProtectedRoute from "./routes/ProtectedRoute";
import ProtectedAdminRoute from "./routes/ProtectedAdminRoute";
import RequireBusiness from "./business/components/RequireBusiness";
import { BusinessMessagingProvider } from "./business/context/BusinessMessagingContext";

// Messaging
import FloatingChat from "./components/messaging/FloatingChat";

// Business
import BusinessRoutes from "./business/businessRoutes";
import BusinessLogin from "./business/pages/BusinessLogin";

// Rider portal
import RiderRoutes from "./rider/riderRoutes";
import RequiredTrackingProvider from "./rider/tracking/RequiredTrackingProvider";

// Supplier portal
import { SupplierAuthProvider } from "./supplier/context/SupplierAuthContext";
import SupplierLogin from "./supplier/pages/SupplierLogin";
import SupplierRegister from "./supplier/pages/SupplierRegister";
import SupplierVerifyEmail from "./supplier/pages/SupplierVerifyEmail";
import SupplierRoutes from "./supplier/supplierRoutes";

// Account Pages
import AccountLayout from "./pages/account/AccountLayout";
import ProfilePage from "./pages/account/ProfilePage";
import AddressesPage from "./pages/account/AddressesPage";
import ChangePasswordPage from "./pages/account/ChangePasswordPage";
import PrivacyPage from "./pages/account/PrivacyPage";
import NotificationsPage from "./pages/account/NotificationsPage";
import NotificationSettingsPage from "./pages/account/NotificationSettingsPage";
import MyPurchasePage from "./pages/account/MyPurchasePage";
import MyApplicationsPage from "./pages/account/MyApplicationsPage";
import WalletPage from "./pages/account/WalletPage";

// Admin
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import BusinessApplications from "./pages/admin/BusinessApplications";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminBusinesses from "./pages/admin/AdminBusinesses";
import AdminAppeals from "./pages/admin/AdminAppeals";
import AdminReports from "./pages/admin/AdminReports";
import AdminCommissionRevenue from "./pages/admin/AdminCommissionRevenue";

// Lazy Loaded Pages
const Home = lazy(() => import("./pages/Home"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Category = lazy(() => import("./pages/Category"));
const Categories = lazy(() => import("./pages/Categories"));
const CartPage = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CheckoutResult = lazy(() => import("./pages/CheckoutResult"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const OrderTrackingPage = lazy(() => import("./pages/account/OrderTrackingPage"));
const SiteInfoPage = lazy(() => import("./pages/SiteInfoPage"));
const SellOnApparifyPage = lazy(() => import("./pages/SellOnApparifyPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const Messages = lazy(() => import("./pages/Messages"));
const SellerStore = lazy(() => import("./pages/SellerStore"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ApplyBusiness = lazy(() => import("./pages/ApplyBusiness"));


/* -------------------------------------------------------
SHOP LAYOUT
Mobile-first layout wrapper
Route-aware shopper chrome mounted from one shared config
------------------------------------------------------- */

function ShopLayout() {
  const { pathname } = useLocation();
  const layoutConfig = getShopperLayoutConfig(pathname);
  const contentPaddingBottomClassName = layoutConfig.showBottomNav
    ? "pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-6"
    : "pb-6";

  return (
    <div className="bg-white text-neutral-900 dark:bg-neutral-900 dark:text-neutral-50">
      <div className="flex min-h-[100dvh] flex-col">
        {layoutConfig.showNavbar && (
          <Navbar
            showSearch={layoutConfig.showSearch}
            searchConfig={layoutConfig.searchConfig}
            {...layoutConfig.navbarActions}
          />
        )}

        <main className="w-full flex-1">
          <div
            className={
              layoutConfig.fullBleed
                ? "w-full"
                : [
                    "mx-auto max-w-7xl px-3 py-4 sm:px-4 sm:py-6 md:px-6 lg:px-8",
                    contentPaddingBottomClassName,
                  ].join(" ")
            }
          >
            <Suspense
              fallback={
                <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6">
                  <FormSkeleton sections={2} label="Loading shopper page" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>

      {layoutConfig.showFooter && <ShopFooter />}

      {layoutConfig.showBottomNav && <BottomNav />}
    </div>
  );
}

function isShopperWidgetRoute(pathname) {
  if (!pathname) return false;

  return (
    pathname === "/" ||
    pathname === "/products" ||
    pathname === "/category" ||
    pathname === "/cart" ||
    pathname === "/checkout" ||
    pathname === "/profile/edit" ||
    pathname.startsWith("/product/") ||
    pathname.startsWith("/category/") ||
    pathname.startsWith("/store/") ||
    pathname.startsWith("/account") ||
    pathname.startsWith("/order-confirmation/")
  );
}

function FloatingChatGate() {
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth();
  const { closeWidget, isWidgetOpen } = useMessaging();
  const [isDesktopViewport, setIsDesktopViewport] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 768px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const syncViewport = (event) => {
      setIsDesktopViewport(event.matches);
    };

    setIsDesktopViewport(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", syncViewport);
      return () => mediaQuery.removeEventListener("change", syncViewport);
    }

    mediaQuery.addListener(syncViewport);
    return () => mediaQuery.removeListener(syncViewport);
  }, []);

  useEffect(() => {
    if (pathname === "/messages") {
      closeWidget();
    }
  }, [closeWidget, pathname]);

  if (!isAuthenticated) return null;
  if (!isShopperWidgetRoute(pathname)) return null;
  if (!isDesktopViewport && !isWidgetOpen) return null;

  return <FloatingChat />;
}

/* -------------------------------------------------------
APP ROOT
------------------------------------------------------- */

export default function App() {
  return (
    <ErrorBoundary>
            <SupplierAuthProvider>
      <BrowserRouter>
        <RequiredTrackingProvider>
                <RouteScrollManager />
                <RouteTitleManager />

                <Suspense
                  fallback={
                    <div className="flex justify-center items-center h-screen">
                      <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  }
                >

                  {/* Floating chat widget (visible only on shopper/base-account routes) */}
                  <FloatingChatGate />

                  <Routes>

                    {/* SHOP AREA */}
                    <Route element={<ShopLayout />}>

                      <Route path="/" element={<Home />} />
                      <Route path="/products" element={<Category discoveryMode="all-products" />} />
                      <Route path="/category" element={<Categories />} />
                      <Route path="/product/:id" element={<ProductDetail />} />
                      <Route path="/category/:slug" element={<Category />} />
                      <Route path="/store/:slug" element={<SellerStore />} />
                      <Route path="/about" element={<SiteInfoPage pageKey="about" />} />
                      <Route path="/about-us" element={<Navigate to="/about" replace />} />
                      <Route path="/sell-on-apparify" element={<SellOnApparifyPage />} />
                      <Route path="/contact-us" element={<SiteInfoPage pageKey="contact" />} />
                      <Route path="/help-center" element={<SiteInfoPage pageKey="help" />} />
                      <Route path="/careers" element={<CareersPage />} />
                      <Route path="/careers/jobs/:jobId" element={<CareersPage />} />
                      <Route path="/careers/action/:token" element={<CareersPage />} />
                      <Route path="/careers/my-applications" element={<CareersPage />} />
                      <Route path="/terms-and-conditions" element={<SiteInfoPage pageKey="terms" />} />
                      <Route path="/privacy-policy" element={<SiteInfoPage pageKey="privacy" />} />
                      <Route path="/shipping-information" element={<SiteInfoPage pageKey="shipping" />} />
                      <Route path="/returns-and-refunds" element={<SiteInfoPage pageKey="returns" />} />
                      <Route
                        path="/cart"
                        element={
                          <ProtectedRoute reason="purchase">
                            <CartPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/checkout"
                        element={
                          <ProtectedRoute reason="purchase">
                            <Checkout />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/checkout/result"
                        element={
                          <ProtectedRoute reason="purchase">
                            <CheckoutResult />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/order-confirmation/:id"
                        element={
                          <ProtectedRoute reason="purchase">
                            <OrderConfirmation />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/messages"
                        element={
                          <ProtectedRoute>
                            <Messages />
                          </ProtectedRoute>
                        }
                      />

                      {/* AUTH */}
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/verify-email" element={<VerifyEmail />} />
                      <Route path="/forgot" element={<ForgotPassword />} />

                  {/* APPLY BUSINESS */}
                  <Route path="/business/register" element={<ApplyBusiness />} />

                  {/* RESUBMIT APPLICATION (protected) */}
                  <Route path="/business/resubmit/:id" element={
                    <ProtectedRoute><ResubmitApplication /></ProtectedRoute>
                  } />

                      {/* ACCOUNT SETTINGS */}
                      <Route
                        path="/account"
                        element={
                          <ProtectedRoute>
                            <AccountLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route
                          index
                          element={<Navigate to="/account/profile" replace />}
                        />
                        <Route path="profile" element={<ProfilePage />} />
                        <Route path="addresses" element={<AddressesPage />} />
                        <Route path="change-password" element={<ChangePasswordPage />} />
                        <Route path="privacy" element={<PrivacyPage />} />
                        <Route path="notifications" element={<NotificationsPage />} />
                        <Route path="applications" element={<MyApplicationsPage />} />
                        <Route path="business-onboarding" element={<Navigate to="/account/profile" replace />} />
                        <Route path="business-onboarding/:membershipId" element={<Navigate to="/account/profile" replace />} />
                        <Route
                          path="notification-settings"
                          element={<NotificationSettingsPage />}
                        />
                        <Route path="wallet" element={<WalletPage />} />
                        <Route path="purchase" element={<MyPurchasePage />} />
                        <Route path="purchase/:id/tracking" element={<OrderTrackingPage />} />
                      </Route>

                      {/* LEGACY PROFILE */}
                      <Route
                        path="/profile/edit"
                        element={
                          <ProtectedRoute>
                            <Navigate to="/account/profile" replace />
                          </ProtectedRoute>
                        }
                      />

                      {/* 404 */}
                      <Route
                        path="*"
                        element={<div className="p-6">Page not found</div>}
                      />

                    </Route>

                    {/* RIDER PORTAL */}
                    <Route path="/rider/*" element={<RiderRoutes />} />

                    {/* BUSINESS LOGIN */}
                    <Route path="/business/login" element={<BusinessLogin />} />

                    {/* BUSINESS PORTAL */}
                    <Route element={<RequireBusiness />}>
                      <Route
                        path="/business/*"
                        element={
                          <BusinessMessagingProvider>
                            <BusinessRoutes />
                          </BusinessMessagingProvider>
                        }
                      />
                    </Route>

                {/* SUPPLIER PORTAL */}
                <Route path="/supplier/login" element={<SupplierLogin />} />
                <Route path="/supplier/register" element={<SupplierRegister />} />
                <Route path="/supplier/verify-email" element={<SupplierVerifyEmail />} />
                <Route path="/supplier/*" element={<SupplierRoutes />} />

                    {/* ADMIN LOGIN */}
                    <Route path="/admin/login" element={<AdminLogin />} />

                    {/* ADMIN PORTAL */}
                    <Route
                      path="/admin"
                      element={
                        <ProtectedAdminRoute>
                          <AdminLayout />
                        </ProtectedAdminRoute>
                      }
                    >
                      <Route
                        index
                        element={<Navigate to="dashboard" replace />}
                      />
                      <Route path="dashboard" element={<AdminDashboard />} />
                      <Route path="applications" element={<BusinessApplications />} />
                      <Route path="users" element={<AdminUsers />} />
                      <Route path="businesses" element={<AdminBusinesses />} />
                      <Route path="commission-revenue" element={<AdminCommissionRevenue />} />
                      <Route path="reports" element={<AdminReports />} />
                  <Route path="appeals" element={<AdminAppeals />} />
                    </Route>

                  </Routes>

                </Suspense>

      </RequiredTrackingProvider>
      </BrowserRouter>
            </SupplierAuthProvider>
    </ErrorBoundary>
  );
}
